import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import { BoutonPrincipal, dateFr, Etiquette } from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { euros } from './NdfMesNotesPage';
import { VerificationNote } from '../../components/ndf/VerificationNote';
import { BlocDelegation } from '../../components/ndf/BlocDelegation';

/**
 * Ce qui attend une décision de ma part.
 *
 * Un valideur ouvre cet écran pour une seule raison : savoir ce qu'il doit
 * regarder. On n'affiche donc QUE les notes qui lui reviennent — celles de son
 * équipe s'il est manager, celles en contrôle RH s'il l'exerce.
 */
export function NdfAValiderPage() {
  const navigate = useNavigate();
  const [verification, setVerification] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'a-valider'],
    queryFn: () => ndfApi.notes.aValider(),
    retry: false,
  });

  if (verification) {
    return <VerificationNote noteId={verification} onFermer={() => setVerification(null)} />;
  }

  const notes = data?.notes ?? [];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-titre text-lg font-bold text-nuit">Notes de frais à valider</h1>
        <p className="text-sm text-nuit/60">
          {notes.length === 0
            ? 'Rien n’attend votre décision'
            : `${notes.length} note${notes.length > 1 ? 's' : ''} en attente`}
        </p>
      </header>

      {/* La délégation se pose ICI : c'est sur cet écran qu'on pense à ses
          approbations, et la veille d'un départ en congés, personne n'ira la
          chercher dans un écran d'administration. */}
      <BlocDelegation />

      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      {!isLoading && !error && notes.length === 0 && (
        <div className="rounded-lg border border-dashed border-nuit/25 bg-white p-8 text-center">
          <p className="text-sm text-nuit/70">
            Aucune note de frais n’attend votre validation.
          </p>
        </div>
      )}

      <ul className="space-y-2">
        {notes.map((n) => (
          <li key={n.id} className="rounded-lg border border-nuit/10 bg-white p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div className="min-w-0">
                <p className="font-titre font-bold text-nuit">{n.salarie_nom}</p>
                <p className="text-sm text-nuit/60">
                  {n.titre ? `${n.titre} · ` : ''}
                  {n.numero}
                  {n.date_note ? ` · ${dateFr(n.date_note)}` : ''}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-medium text-nuit">{euros(n.total_a_rembourser)}</p>
                <p className="text-xs text-nuit/60">à rembourser</p>
              </div>
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-1">
              <Etiquette ton="actif">
                {n.etape === 'manager' ? 'Approbation manager' : 'Contrôle RH'}
              </Etiquette>
              <Etiquette>
                {n.nombre_depenses} dépense{n.nombre_depenses > 1 ? 's' : ''}
              </Etiquette>
              {/* Le contrôle RH exige que TOUTES les dépenses soient vues :
                  afficher l'avancement évite de chercher lesquelles manquent. */}
              {n.etape === 'rh' && (
                <Etiquette ton={n.controlees === n.nombre_depenses ? 'positif' : 'attention'}>
                  {n.controlees}/{n.nombre_depenses} contrôlées
                </Etiquette>
              )}
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              <BoutonPrincipal type="button" onClick={() => setVerification(n.id)}>
                Démarrer la vérification
              </BoutonPrincipal>
              <button
                type="button"
                onClick={() => navigate(`/notes-de-frais/note/${n.id}`)}
                className="min-h-11 text-sm text-nuit underline"
              >
                Parcourir librement
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
